import useCampaignBuilder from "Hooks/useCampaignBuilder";
import TargetingCampaignView_AUDIENCES from "./TargetingCampaignView_AUDIENCES";
import TargetingCampaignView_GEOGRAPHY from "./TargetingCampaignView_GEOGRAPHY";
import TargetingCampaignView_DEMOGRAPHICS from "./TargetingCampaignView_DEMOGRAPHICS";
import { clsx, makeKey } from "modules/Utils";
import Sty from "modules/Sty";
import CardSymphony from "elements/CardSymphony";
import ButtonSymphony from "elements/ButtonSymphony";
import Banner from "elements/Banner";
import TagSymphony from "elements/TagSymphony";
import ModalSymphony from "elements/ModalSymphony";
import { CSSProperties, useEffect, useState } from "react";
import TargetingCampaignView_PREVIEW from "./TargetingCampaignView_PREVIEW";

interface TargetingCampaignViewSummaryProps {
    className: string;
    platform: 'tiktok' | 'facebook';
}

// NOTE: component()
export const TargetingCampaignViewSummary = ({
    className,
    platform,
}: TargetingCampaignViewSummaryProps) => {
    const {
        minAge,
        maxAge,
        genders,
        languages,
        geographyBuckets,
        selectedAudienceGroups,
        resetDemographicsToRecommended,
        resetAudiencesToRecommended,
        resetGeographyBuckets,
        showEditGeographyBucketsBanner,
        setShowEditGeographyBucketsBanner,
        isGeographyBucketsObjectValid,
    } = useCampaignBuilder()
    
    const [isDemographicsModalOpen, setIsDemographicsModalOpen] = useState(false);
    const [isGeographyModalOpen, setIsGeographyModalOpen] = useState(false);
    const [isAudienceModalOpen, setIsAudienceModalOpen] = useState(false);

    useEffect(() => {
        const allGeographiesEmpty = geographyBuckets.every(bucket => bucket.geographies.length === 0);
        setShowEditGeographyBucketsBanner(allGeographiesEmpty);
    }, []);

    const renderGeographySummary = () => {
        return (
            <div className="geographiesSummary" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {geographyBuckets.map((bucket, index) => {
                    const {
                        name,
                        percentage,
                        geographies,
                        id,
                    } = bucket;
                    return (
                        <Banner
                            key={makeKey('geography-bucket', id, index)}
                            color="none"
                            className="geographySummaryBanner"
                            style={{
                                border: Sty.border,
                            }}
                        >
                            <div style={{ display: 'flex', flexDirection: 'row', gap: 8, alignItems: 'center', justifyContent: 'space-between' }}>
                                <p style={{ ...Sty.text18bold }}>{name}</p>
                                <p>{percentage}% of your budget</p>
                            </div>

                            <div>
                                <p style={{ ...Sty.text16, marginBottom: 8 }}>
                                    This budget will be spent to target audiences in the following locations:
                                </p>
                                <div
                                    className="demographicsSummaryContent"
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        gap: 8,
                                        alignItems: 'center',
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    {geographies.map((geography, index) => {
                                        const nameToDisplay = geography.name || geography.id
                                        return (
                                            <TagSymphony
                                                key={makeKey('geography', geography.id, index)}
                                                className="demographicsSummaryTag"
                                                title={`${nameToDisplay} (${geography.type})`}
                                                color="purple"
                                            />
                                        )
                                    })}
                                </div>
                            </div>
                        </Banner>
                    )
                })}
                <ButtonSymphony
                    className="demographicsSummaryEditButton"
                    style={{ marginTop: 16, marginLeft: 'auto', marginRight: 'auto' }}
                    variant="outlined"
                    onClick={() => {
                        setIsGeographyModalOpen(true)
                    }}
                >
                    Edit
                </ButtonSymphony>
            </div>
        )
    }

    const renderSectionHeader = (title: string, editButtonText: string) => {
        return (
            <div style={{
                borderBottom: Sty.border,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingBottom: 8,
                ...Sty.text18bold,
            }}>
                <p className="demographicsSummaryTitle"
                >{title}</p>
                <ButtonSymphony
                    className="demographicsSummaryEditButton"
                    variant="outlined"
                    onClick={() => {
                        // setCurrentStep(PlaybookStepName.TARGETING)
                    }}
                >
                    {editButtonText}
                </ButtonSymphony>
            </div>
        )
    }

    const renderAgeGenderSummary = () => {
        if (platform === 'tiktok') return null;
        let genderText;
        if (genders.length === 1) {
            genderText = genders[0].name;
        } else if (genders.length === 2) {
            genderText = genders.map(gender => gender.name).join(' & ');
        } else {
            genderText = 'All genders';
        }
        const languageText = languages.length > 0 ? languages.map(language => language.name).join(', ') : 'English';
        const summaryTitle = `${genderText} / Ages ${minAge} to ${maxAge} / ${languageText} speakers`;

        let showLanguages = platform === 'facebook' ? true : false;
        return (
            <CardSymphony
                className={clsx("targetingCampaignViewSummary", className)}
                title="Age, Gender, & Languages"
                subtitle="We recommend these demographics to get the best performance:"
                size="small"
                isPadding16
            >
                <div className="demographicsSummary">
                    <div className="demographicsSummaryContent"
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 8,
                            alignItems: 'flex-start',
                            width: '100%',
                            marginTop: 8,
                        }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            borderTop: Sty.border,
                            paddingTop: 12,
                            flexDirection: 'row', gap: 8, alignItems: 'center', borderBottom: Sty.border, paddingBottom: 12, width: '100%'
                        }}>
                            <p>Genders:</p>
                            <p style={{ ...Sty.text16bold }}>{genderText}</p>
                            {/* <TagSymphony
                                className="demographicsSummaryTag"
                                title={genderText}
                                color="purple"
                            /> */}
                        </div>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            flexDirection: 'row', gap: 8, alignItems: 'center', borderBottom: Sty.border, paddingBottom: 12, width: '100%'
                        }}>
                            <p>Ages:</p>
                            <p style={{ ...Sty.text16bold }}>{`${minAge} to ${maxAge}`}</p>
                        </div>
                        {showLanguages && (<div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            flexDirection: 'row', gap: 8, alignItems: 'center', borderBottom: Sty.border, paddingBottom: 12, width: '100%'
                        }}>
                            <p>Languages:</p>
                            <p style={{ ...Sty.text16bold }}>{languageText}</p>
                        </div>)}
                    </div>
                    <ButtonSymphony
                        className="demographicsSummaryEditButton"
                        variant="outlined"
                        onClick={() => {
                            setIsDemographicsModalOpen(true)
                        }}
                        style={{ marginTop: 16, marginLeft: 'auto', marginRight: 'auto' }}
                    >
                        Edit
                    </ButtonSymphony>
                </div >
            </CardSymphony>
        )
    }

    const renderAudiencesSummary = () => {
        return (
            <div className="audiencesSummary" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={Sty.text16bold}>Audience Groups:</div>
                {selectedAudienceGroups.map((group, index) => {
                    return (
                        <Banner
                            key={makeKey(group.title, index)}
                            color="none"
                            className="audienceSummaryBanner"
                            style={{
                                border: Sty.border,
                            }}
                        >
                            <div
                                className="audienceSummaryBannerContent"
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    gap: 8,
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    ...Sty.text18bold
                                }}
                            >
                                {group.title}
                            </div>

                            <div className="audienceSummaryBannerContentInner">
                                <div style={{ ...Sty.text16, marginBottom: 8 }}>
                                    This audience includes:
                                </div>
                                <div className="audiencesSummaryContent"
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        gap: 8,
                                        alignItems: 'center',
                                        flexWrap: 'wrap',
                                    }}>
                                    {group.tags.map((tag, tagIndex) => {
                                        return (
                                            <TagSymphony
                                                key={makeKey(tag.title, tagIndex)}
                                                className="audiencesSummaryTag"
                                                title={tag.title}
                                                color="purple"
                                            />
                                        )
                                    })}
                                </div>
                            </div>
                        </Banner>
                    )
                })}
                <ButtonSymphony
                    className="audiencesSummaryEditButton"
                    variant="outlined"
                    onClick={() => {
                        setIsAudienceModalOpen(true)
                    }}
                    style={{ marginTop: 16, marginLeft: 'auto', marginRight: 'auto' }}
                >
                    Edit
                </ButtonSymphony>
            </div>
        )
    }

    const renderModals = () => {
        return (
            <>
                <ModalSymphony
                    className="targetingCampaignViewDemographicsModal"
                    title="Edit Demographics Targeting"
                    subtitle="Adjust the ages, genders, and languages targeted in your campaign."
                    size="medium"
                    isOpen={isDemographicsModalOpen}
                    onClose={() => setIsDemographicsModalOpen(false)}
                    buttonLeftText="Reset to Recommended"
                    buttonRightText="Close"
                    buttonLeftVariant="text"
                    onClickButtonLeft={() => {
                        resetDemographicsToRecommended()
                    }}
                    onClickButtonRight={() => {
                        setIsDemographicsModalOpen(false)
                    }}
                    buttonRightVariant="primary"
                >
                    <TargetingCampaignView_DEMOGRAPHICS
                        className="targetingCampaignViewDemographics_GrowPlaylistFollowers"
                        platform={platform}
                    />
                </ModalSymphony>

                <ModalSymphony
                    className="targetingCampaignViewGeographyModal"
                    title="Edit Geography Targeting"
                    subtitle="Adjust the locations targeted in your campaign."
                    size="medium"
                    isOpen={isGeographyModalOpen}
                    onClose={() => setIsGeographyModalOpen(false)}
                    buttonLeftText="Reset to Recommended"
                    buttonRightDisabled={!isGeographyBucketsObjectValid}
                    buttonRightText="Close"
                    buttonLeftVariant="text"
                    onClickButtonLeft={() => {
                        resetGeographyBuckets()
                    }}
                    onClickButtonRight={() => {
                        setIsGeographyModalOpen(false)
                    }}
                    buttonRightVariant="primary"
                >
                    <TargetingCampaignView_GEOGRAPHY
                        className="targetingCampaignViewGeography_GrowPlaylistFollowers"
                        style={{ marginBottom: 16 }}
                        platform={platform}
                        hideTitle
                    />
                </ModalSymphony>

                <ModalSymphony
                    className="targetingCampaignViewAudiencesModal"
                    title="Edit Audiences Targeting"
                    subtitle="Adjust the audiences targeted in your campaign."
                    size="medium"
                    isOpen={isAudienceModalOpen}
                    onClose={() => setIsAudienceModalOpen(false)}
                    buttonLeftText="Reset to Recommended"
                    buttonRightText="Close"
                    buttonLeftVariant="text"
                    onClickButtonLeft={() => {
                        resetAudiencesToRecommended()
                    }}
                    onClickButtonRight={() => {
                        setIsAudienceModalOpen(false)
                    }}
                    buttonRightVariant="primary"
                >
                    <TargetingCampaignView_AUDIENCES
                        hideTitle
                        className="targetingCampaignViewAudiences_GrowPlaylistFollowers"
                        platform={platform}
                    />
                    <TargetingCampaignView_PREVIEW
                        className="targetingCampaignViewPreview_GrowPlaylistFollowers"
                        platform={platform}
                    />
                </ModalSymphony>
            </>
        )
    }

    // NOTE: render()
    return (
        <>
            {renderAgeGenderSummary()}

            <CardSymphony
                className={clsx("targetingCampaignViewSummary", className)}
                title="Locations"
                subtitle="We recommend these locations to get the best performance:"
                size="small"
                isPadding16
            >
                {showEditGeographyBucketsBanner &&
                    <Banner
                        className="geographySummaryBanner"
                        color="red"
                        title="No locations selected"
                    >
                        Add locations by clicking Edit
                    </Banner>
                }

                {renderGeographySummary()}
            </CardSymphony>

            <CardSymphony
                className={clsx("targetingCampaignViewSummary", className)}
                title="Targeted Audiences"
                subtitle="We recommend these audiences to get the best performance. This is based on your linked platforms, and it's the right targeting for you."
                size="small"
                isPadding16
            >
                {renderAudiencesSummary()}
            </CardSymphony>

            {renderModals()}
        </>
    )
}

const sty: Record<string, CSSProperties> = {}